'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BulkShipment {
  id: string;
  trackingNumber: string;
  customer: string;
  status: string;
  selected: boolean;
}

const mockBulkShipments: BulkShipment[] = [
  {
    id: '1',
    trackingNumber: 'TRK-2026-001234',
    customer: 'John Doe',
    status: 'In Transit',
    selected: false,
  },
  {
    id: '2',
    trackingNumber: 'TRK-2026-005678',
    customer: 'Sarah Johnson',
    status: 'In Transit',
    selected: false,
  },
  {
    id: '3',
    trackingNumber: 'TRK-2026-009012',
    customer: 'Michael Chen',
    status: 'Pending Pickup',
    selected: false,
  },
  {
    id: '4',
    trackingNumber: 'TRK-2026-003456',
    customer: 'Emily Brown',
    status: 'In Transit',
    selected: false,
  },
  {
    id: '5',
    trackingNumber: 'TRK-2026-007890',
    customer: 'David Wilson',
    status: 'Customs Clearance',
    selected: false,
  },
];

const statuses = [
  'Pending Pickup',
  'In Transit',
  'Customs Clearance',
  'Out for Delivery',
  'Delivered',
  'On Hold',
];

const drivers = [
  { id: '1', name: 'Driver A - John Smith' },
  { id: '2', name: 'Driver B - Maria Garcia' },
  { id: '3', name: 'Driver C - David Lee' },
  { id: '4', name: 'Driver D - Sarah Wilson' },
];

export default function BulkOperations() {
  const [shipments, setShipments] = useState<BulkShipment[]>(mockBulkShipments);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkDriver, setBulkDriver] = useState('');
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const selectedCount = shipments.filter((s) => s.selected).length;

  const handleSelectAll = () => {
    const allSelected = shipments.every((s) => s.selected);
    setShipments(shipments.map((s) => ({ ...s, selected: !allSelected })));
  };

  const handleSelectShipment = (id: string) => {
    setShipments(shipments.map((s) => (s.id === id ? { ...s, selected: !s.selected } : s)));
  };

  const handleBulkApply = () => {
    if (selectedCount === 0) {
      alert('Please select at least one shipment');
      return;
    }

    if (bulkAction === 'status' && !bulkStatus) {
      alert('Please select a status');
      return;
    }

    if (bulkAction === 'assign' && !bulkDriver) {
      alert('Please select a driver');
      return;
    }

    setProcessing(true);

    setTimeout(() => {
      if (bulkAction === 'status') {
        setShipments(
          shipments.map((s) => (s.selected ? { ...s, status: bulkStatus, selected: false } : s))
        );
        setSuccessMessage(`Successfully updated status for ${selectedCount} shipments`);
      } else if (bulkAction === 'assign') {
        setShipments(shipments.map((s) => (s.selected ? { ...s, selected: false } : s)));
        setSuccessMessage(`Successfully assigned ${selectedCount} shipments to driver`);
      }

      setProcessing(false);
      setBulkAction('');
      setBulkStatus('');
      setBulkDriver('');

      setTimeout(() => setSuccessMessage(''), 3000);
    }, 1500);
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
          <Icon name="CheckCircleIcon" size={24} className="text-success" />
          <p className="text-sm font-medium text-success">{successMessage}</p>
        </div>
      )}

      {/* Bulk Actions Panel */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6">Bulk Operations</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Action Type
            </label>
            <select
              value={bulkAction}
              onChange={(e) => {
                setBulkAction(e.target.value);
                setBulkStatus('');
                setBulkDriver('');
              }}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="">Select Action</option>
              <option value="status">Update Status</option>
              <option value="assign">Assign Driver</option>
            </select>
          </div>

          {bulkAction === 'status' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                New Status
              </label>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              >
                <option value="">Select Status</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          )}

          {bulkAction === 'assign' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Assign To
              </label>
              <select
                value={bulkDriver}
                onChange={(e) => setBulkDriver(e.target.value)}
                className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              >
                <option value="">Select Driver</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-end">
            <button
              onClick={handleBulkApply}
              disabled={processing || selectedCount === 0}
              className="w-full px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Icon name="BoltIcon" size={20} />
                  <span>Apply to {selectedCount} Selected</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Shipments Selection Table */}
      <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Select Shipments</h3>
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              <Icon
                name={shipments.every((s) => s.selected) ? 'CheckCircleIcon' : 'CircleStackIcon'}
                size={20}
              />
              <span>{shipments.every((s) => s.selected) ? 'Deselect All' : 'Select All'}</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground w-12">
                  <input
                    type="checkbox"
                    checked={shipments.every((s) => s.selected)}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Tracking Number
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Customer
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Current Status
                </th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((shipment) => (
                <tr
                  key={shipment.id}
                  className={`border-t border-border hover:bg-muted/30 transition-colors ${
                    shipment.selected ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="py-4 px-6">
                    <input
                      type="checkbox"
                      checked={shipment.selected}
                      onChange={() => handleSelectShipment(shipment.id)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    />
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm font-mono font-medium text-primary">
                      {shipment.trackingNumber}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-foreground">{shipment.customer}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-muted-foreground">{shipment.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">Bulk Operations Guide</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Select multiple shipments using checkboxes</li>
              <li>• Choose an action type (Update Status or Assign Driver)</li>
              <li>• Select the target status or driver</li>
              <li>• Click "Apply" to execute the bulk operation</li>
              <li>• All selected shipments will be updated simultaneously</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}